import { Box, Button, Flex, Grid, IconButton, Input, Stack, Switch, Text, useToast } from "@chakra-ui/react";
import { FaEdit, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import React, { useState, ChangeEvent, useEffect, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { RxCross1 } from "react-icons/rx";
import configService from "../../api/configService";
import creditDeductionService from "../../api/creditDeductionService";
import { interceptorsSetup } from "../../api/intreceptor";
import LoadingOverlay from "../../reusable-components/LoadingOverlay/LoadingOverlay";
import useFetchConfigSetting from "../../hooks/useConfigSetting";
import { CentralizeStore } from "../../contexts/CentralizeStoreProvider";
import { useConfirm } from "../../reusable-components/confirm-dialog/ConfirmDialogProvider";
import ReactQuill from "react-quill";
import { FormControl, FormLabel } from "react-bootstrap";

interface ConfigItem {
  id: string;
  [key: string]: any;
}

const CREDIT_RATE_MODE_LABELS: Record<string, string> = {
  tag: "Tag Generation",
  description: "Description Generation",
  title: "Title Generation",
  image_generation: "Image Generation",
};

const Configuration: React.FC = () => {
  const [configData, setConfigData] = useState([]);
  const [editId, setEditId] = useState<string | null>(null);
  const [tempData, setTempData] = useState<ConfigItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [usedUserLicense, setUsedUserLicense] = useState<number | null>(null);
  const [usedCreditPool, setUsedCreditPool] = useState<number | null>(null);
  const [warningMsg, setWarningMsg] = useState("")
  const [isWarning, setIsWaring] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false);
  const { fetchConfigSetting, configurationSetting } = useFetchConfigSetting()
  const comp_id = localStorage.getItem("comp_id")
  const role = localStorage.getItem("role")
  const [originalCustomPrompts, setOriginalCustomPrompts] = useState([]);
  const { setFeatureSearch, setFeatureGenerateTag, setDomainByAI } = useContext(CentralizeStore);
  const [customPrompts, setCustomPrompts] = useState([]);
  const[loadingOverlay, setLoadingOverlay] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  // Per-mode generation credit rates (tag/description/title/image_generation) -
  // replaces the old flat "Tags Deduction" (deductiontags) card, which is no
  // longer read by tag generation (deduction now happens per-mode at job
  // completion, see docs/credit-system-plan.md). Kept as separate state from
  // configData since it's a different backend resource (credit_deduction
  // table, not company_config), not a company_config edit.
  const [creditRates, setCreditRates] = useState([]);
  const [tempCreditRates, setTempCreditRates] = useState([]);
  const [isEditingCreditRates, setIsEditingCreditRates] = useState(false);


  useEffect(() => {
    interceptorsSetup(navigate);
  }, [navigate]);

  useEffect(() => {
    // fetchConfigSetting()
    handleGetConfigData();
    fetchCreditRates();
  }, []);

  useEffect(() => {
    if (
      tempData &&
      tempData.Total_User_License !== undefined &&
      tempData.Pending_User_License !== undefined
    ) {
      setUsedUserLicense(
        tempData.Total_User_License - tempData.Pending_User_License
      );
    }
    if (
      tempData &&
      tempData.Total_Credit_Pool !== undefined &&
      tempData.Balance_Credit_Pool !== undefined
    ) {
      setUsedCreditPool(
        tempData.Total_Credit_Pool - tempData.Balance_Credit_Pool
      );
    }
  }, [tempData]);

  const stripHtml = (html: string | undefined | null) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const handleGetConfigData = async () => {
    try {
      setIsLoading(true);
      setLoadingOverlay(true);
      const response = await configService.getConfigData(comp_id);
      if (response.status === 200) {
        let configList = response.data.config;
        let featureList = configList.filter((item) => item.id === "features");
        setFeatureGenerateTag(featureList[0].Intelli_Tag);
        setFeatureSearch(featureList[0].Intelli_Search);
        setDomainByAI(featureList[0].Domain_by_AI);
        let descriptionPrompt = configList.find((item) => item.id === "descriptionPrompt")
        let transformedData = [];
        if (descriptionPrompt?.Prompt_Tag.length > 0) {
          let companiesObj = descriptionPrompt.Prompt_Tag[0]; // Get the first (and only) object

          // Convert each key-value pair into { Company: key, Prompt: value }
          transformedData = Object.entries(companiesObj).map(([company, prompt]) => ({
            Company: company,
            Prompt: prompt
          }));
        }
        setCustomPrompts(transformedData);
        setConfigData(response.data.config);
        fetchConfigSetting()
      }
    } catch (err) {
      console.log("error while getting config data:", err);
    } finally {
      setIsLoading(false);
      setLoadingOverlay(false);
    }
  };

  const fetchCreditRates = async () => {
    try {
      const response = await creditDeductionService.getRates(comp_id);
      if (response.status === 200) {
        setCreditRates(response.data.rates);
      }
    } catch (err) {
      console.log("error while getting credit deduction rates:", err);
    }
  };

  const handleCreditRatesEditClick = () => {
    setTempCreditRates(creditRates.map((r) => ({ ...r })));
    setIsEditingCreditRates(true);
  };

  const handleCreditRatesCancel = () => {
    setIsEditingCreditRates(false);
  };

  const handleCreditRatesChange = (mode: string, key: "to_be_deducted" | "credit_amount", value: boolean | string) => {
    setTempCreditRates((prev) =>
      prev.map((r) =>
        r.mode === mode
          ? { ...r, [key]: key === "credit_amount" ? (value === "" ? 0 : parseFloat(value as string)) : value }
          : r
      )
    );
  };

  const handleCreditRatesSave = async () => {
    setLoadingOverlay(true);
    try {
      const response = await creditDeductionService.updateRates({
        comp_id, email: localStorage.getItem("email"), rates: tempCreditRates,
      });
      if (response.status === 200) {
        setCreditRates(tempCreditRates);
        setIsEditingCreditRates(false);
        showToast("Credit deduction rates updated successfully!");
      }
    } catch (err) {
      console.log("error while updating credit deduction rates:", err);
    } finally {
      setLoadingOverlay(false);
    }
  };

  const handleEditClick = (id: string) => {
    setIsWaring(false)
    setEditId(id);
    const currentItem = configData.find((item) => item.id === id) || null;
    setTempData(currentItem);
    setOriginalCustomPrompts([...customPrompts]);
  };

  const handleChange = (key: string, value: string | boolean, size) => {
    if (tempData) {
      let newTotal: number;
      if (key === "Total_User_License") {
        newTotal = value === "" ? 0 : Math.floor(parseFloat(value as string));
        if (usedUserLicense !== null && newTotal < usedUserLicense) {
          setIsWaring(true)
          setWarningMsg(`Total User License cannot be less than Used User License (${usedUserLicense}).`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        const newPending = newTotal - usedUserLicense!;
        setTempData({
          ...tempData,
          Total_User_License: +(newTotal),
          Pending_User_License: +(newPending),
        });
      } else if (key === "Total_Credit_Pool") {
        const newTotal = value === "" ? 0 : parseFloat(value as string);
        if (usedCreditPool !== null && newTotal < usedCreditPool) {
          setIsWaring(true)
          setWarningMsg(`Total Credit Pool cannot be less than Used Credit Pool (${usedCreditPool}).`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        const newBalance = newTotal - usedCreditPool!;
        setTempData({
          ...tempData,
          Total_Credit_Pool: +(newTotal),
          Balance_Credit_Pool: +(newBalance),
        });
      } else if (key === "Max_Image_Size_Mb") {
        const newSize = value === "" ? 0 : parseFloat(value as string);
        if (newSize > size) {
          setIsWaring(true)
          setWarningMsg(`Max Image Size must be between 0 and ${size} MB.`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        setTempData({
          ...tempData,
          Max_Image_Size_Mb: +(newSize),
          Max_Size: size
        });
      } else if (key === "Max_Image_Quality_Percent") {
        const newQuality = value === "" ? 0 : parseFloat(value as string);
        if (newQuality > size) {
          setIsWaring(true)
          setWarningMsg(`Max Image Quality Size must be between 0 and ${size} MB.`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        setTempData({
          ...tempData,
          Max_Image_Quality_Percent: +(newQuality),
          Max_Quality: size
        });
      } else if (key === "Max_Image_Width") {
        const newQuality = value === "" ? 0 : parseFloat(value as string);
        if (newQuality > size) {
          setIsWaring(true)
          setWarningMsg(`Max Image Width Size must be between 0 and ${size} MB.`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        setTempData({
          ...tempData,
          Max_Image_Width: +(newQuality),
          Max_Width: size
        });
      } else if (key === "Max_Image_Height") {
        const newQuality = value === "" ? 0 : parseFloat(value as string);
        if (newQuality > size) {
          setIsWaring(true)
          setWarningMsg(`Max Image Height must be between 0 and ${size} MB.`)
        } else {
          setIsWaring(false)
          setWarningMsg("")
        }
        setTempData({
          ...tempData,
          Max_Image_Height: +(newQuality),
          Max_Height: size
        });
      } else if (key === 'Credit_Deduction') {
        const newCredit = value === "" ? 0 : parseFloat(value as string);
        setTempData({
          ...tempData,
          Credit_Deduction: +(newCredit)
        });
      } else if (key === 'Default_Credit') {
        const newDefaultCredit = value === '' ? 0 : parseFloat(value as string);
        setTempData({
          ...tempData,
          Default_Credit: +(newDefaultCredit)
        });
      } else {
        setTempData({
          ...tempData,
          [key]: value,
        });
      }
    }
  };

  const handleSave = async () => {

    if (!tempData) return;
    setLoadingOverlay(true);
    try {

      let invalidPrompts = [];
      customPrompts.forEach((item) => {
        if (!item.Company.trim()) {
          invalidPrompts.push(` Company is required.`);
        }
        if (!item.Prompt.trim()) {
          invalidPrompts.push(`Prompt text is required.`);
        }
      });

      if (invalidPrompts.length > 0) {
        toast({
          title: "Validation Error",
          description: invalidPrompts.join("\n"),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        return;
      }
      setIsUpdating(true);

      // ✅ Step 1: Filter tempData
      const filteredTempData = Object.keys(tempData)
        .filter((key) => keysToDisplay.includes(key) || key === "id")
        .reduce((obj, key) => {
          obj[key] = tempData[key];
          return obj;
        }, {} as ConfigItem);

      setConfigData(
        configData.map((item) => (item.id === editId ? filteredTempData : item))
      );

      setEditId(null);
      setIsWaring(false);
      setWarningMsg("");


      // ✅ Step 2: First Payload - Filtered Data
      const filteredPayload = { items: [filteredTempData], comp_id };

      // ✅ Step 3: Second Payload - Generate Description
      const valuesObject = customPrompts.reduce((acc, item) => {
        if (item.Company) {
          acc[item.Company] = stripHtml(item.Prompt || "");
        }
        return acc;
      }, {});
      const valuesObjectTag = customPrompts.reduce((acc, item) => {
        if (item.Company) {
          acc[item.Company] = (item.Prompt || "");
        }
        return acc;
      }, {});

      const generateDescriptionPayload = {
        items: [
          {
            id: "descriptionPrompt",
            Prompt: [valuesObject],
            Prompt_Tag: [valuesObjectTag]
          }
        ],
        comp_id,
      };


      const [filteredRes, generatedRes] = await Promise.all([
        configService.updateConfig(filteredPayload),
        configService.updateConfig(generateDescriptionPayload),
      ]);

      if (filteredRes.status === 200 && generatedRes.status === 200) {
        await handleGetConfigData();
        showToast("Configuration updated successfully!");
      }
    } catch (err) {
      console.log("Error while updating config:", err);
    } finally {
      setIsUpdating(false);
      setLoadingOverlay(false);
    }
  };


  const showToast = (message: string) => {
    toast({
      title: message,
      position: "top",
      status: "success",
      duration: 3000,
      variant: "left-accent",
      isClosable: true,
    });
  };



  const handleCancel = () => {
    setEditId(null);
    setTempData(null);
    setIsWaring(false)
    setWarningMsg("")
    setCustomPrompts(originalCustomPrompts);
  };

  const isValid = () => {
    if (tempData) {
      if (tempData.Total_User_License !== undefined && tempData.Pending_User_License !== undefined) {
        return tempData.Total_User_License >= usedUserLicense!;
      }
      if (tempData.Total_Credit_Pool !== undefined && tempData.Balance_Credit_Pool !== undefined) {
        return tempData.Total_Credit_Pool >= usedCreditPool!;
      }
      if (tempData.Max_Image_Size_Mb !== undefined && tempData.Max_Size !== undefined) {
        return tempData.Max_Image_Size_Mb >= 0 && tempData.Max_Image_Size_Mb <= tempData.Max_Size;
      }
      if (tempData.Max_Image_Quality_Percent !== undefined && tempData.Max_Quality !== undefined) {
        return tempData.Max_Image_Quality_Percent >= 0 && tempData.Max_Image_Quality_Percent <= tempData.Max_Quality;
      }
      if (tempData.Max_Image_Width !== undefined && tempData.Max_Width !== undefined) {
        return tempData.Max_Image_Width >= 0 && tempData.Max_Image_Width <= tempData.Max_Width;
      }
      if (tempData.Max_Image_Height !== undefined && tempData.Max_Height !== undefined) {
        return tempData.Max_Image_Height >= 0 && tempData.Max_Image_Height <= tempData.Max_Height;
      }
      return true;
    }
    return false;
  };

  const isBoolean = (value: any) => typeof value === "boolean";
  const keysToDisplay = [
    "Total_User_License",
    "Pending_User_License",
    "Default_Credit",
    "Use_Credit_Pool",
    "Total_Credit_Pool",
    "Balance_Credit_Pool",
    "Credit_Deduction",
    "Max_Image_Size_Mb",
    "Max_Image_Quality_Percent",
    "Max_Image_Width",
    "Max_Image_Height",
    "To_be_Deducted",
    "Credit_Deduction",
    "Intelli_Tag",
    "Intelli_Search",
    "Relevancy_Threshold",
    "Domain_by_AI",
    "AutoDescriptionGeneration",
    "AutoTitleGeneration",
    "Prompt_Tag"
  ];


  const formatConfigName = (name) => {
    switch (name) {
      case 'license':
        return 'License';
      case 'creditpool':
        return 'Credit Pool';
      case 'imagequality':
        return 'Image Quality';
      case 'deductionupload':
        return 'Upload Deduction';
      case 'deductiontags':
        return 'Tags Deduction';
      case 'deductionsearch':
        return 'Search Deduction';
      case 'features':
        return 'Features';
      case 'autodescriptiongeneration':
        return 'Auto Description Generation';
      case 'autotitlegeneration':
        return 'Auto Title Generation';
      case 'descriptionPrompt':
        return ' Description Prompt';
      default:
        return name;
    }
  };
  const superAdminKeys = keysToDisplay;
  const adminKeys = [
    "Temperature",
    "No_Of_Chunks",
  ];
  const getVisibleKeys = () => {
    if (role === 'superAdmin') return superAdminKeys;
    if (role === 'Admin') return adminKeys;
    return [];
  };
  const visibleKeys = getVisibleKeys();

  const customPromptItem = configData.find(item => item.id === 'descriptionPrompt');


useEffect(() => {
  if (customPromptItem?.values?.length > 0) {
    setCustomPrompts(customPromptItem.values);
  } else if (customPrompts.length === 0) {
    setCustomPrompts([{ Company: "", Prompt: "" }]);
  }
}, [customPromptItem]);

  const filteredConfigData = configData.filter(item => item.id !== 'descriptionPrompt' && item.id !== 'deductionupload');
  const handleDelete = async (index) => {
    const prompt = customPrompts[index];
    const name = String(prompt?.Company ?? "").trim();
    // A prompt that was just added and is still blank needs no "are you sure".
    if (name !== "" || String(prompt?.Prompt ?? "").trim() !== "") {
      const confirmed = await confirm({
        title: "Delete this description prompt?",
        message: `${name ? `The "${name}" description prompt` : "This description prompt"} will be deleted.\nThe change is saved when you click Save; Cancel brings it back.`,
        confirmLabel: "Delete",
      });
      if (!confirmed) return;
    }
    setCustomPrompts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddNew = () => {
    const newItem = { Company: "", Prompt: "" };
    setCustomPrompts([...customPrompts, newItem]);
  };

  const handleCompanyChange = (key, value, index) => {
    setCustomPrompts((prevPrompts) =>
      prevPrompts.map((item, i) => {
        if (i === index) {
          return { ...item, [key]: value }; // Update only the specific index
        }
        return item;
      })
    );
  };

  return (
    <Box p={2}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        pb={2}
        borderBottom="0.5px solid #BDBDBD"
      >
        <h2>Configuration</h2>
        <NavLink to="/layout" className="crossBtn">
          <span><RxCross1 /></span>
        </NavLink>
      </Flex>
      <Grid gridTemplateColumns={{ base: "repeat(1,1fr)", md: "repeat(2,1fr)", lg: "repeat(3,1fr)" }} columnGap={"12px"}>

        {filteredConfigData.map((item) => { // Using filteredConfigData to exclude generate_description
          if (item.id === 'deductiontags' && role === 'superAdmin') {
            // deductiontags (company_config) is no longer read by tag generation -
            // deduction now happens per-mode at job completion (see
            // docs/credit-system-plan.md), driven by the credit_deduction table
            // instead. This card replaces it in the same grid position, same
            // visual chrome, but edits credit_deduction via its own service/state.
            // Gated to superAdmin explicitly (matching the card it replaces,
            // which was implicitly hidden from Admin because 'To_be_Deducted'/
            // 'Credit_Deduction' aren't in adminKeys) - without this check
            // Admin would see and could edit every company's billing rates,
            // a real capability escalation versus the old card's behavior.
            const displayRates = isEditingCreditRates ? tempCreditRates : creditRates;
            return (
              <Box key="creditDeductionRates" p={4} shadow="md" borderWidth="1px" mb={4} borderRadius={"5px"}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text fontWeight={700} fontSize="bold" m={"0px"}>Generation Deduction Rates</Text>
                  {isEditingCreditRates ? (
                    <Flex flexDir={{ base: "column", md: "row" }} gap={{ base: "7px", md: "auto" }}>
                      <Button size={{ base: "sm" }} id="btn" variant="solid" onClick={handleCreditRatesSave}>
                        Save
                      </Button>
                      <Button size={{ base: "sm" }} colorScheme="red" variant="outline" onClick={handleCreditRatesCancel}>
                        Cancel
                      </Button>
                    </Flex>
                  ) : (
                    <IconButton aria-label="Edit" icon={<FaEdit />} onClick={handleCreditRatesEditClick} />
                  )}
                </Flex>
                <Stack spacing={1} mt={3}>
                  {displayRates.map((rate) => (
                    <Grid
                      gap={{ base: "auto", md: "7px", lg: "auto" }}
                      gridTemplateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)" }}
                      key={rate.mode}
                    >
                      <Text fontWeight="sm" m={"0px"}>{CREDIT_RATE_MODE_LABELS[rate.mode] || rate.mode}</Text>
                      {isEditingCreditRates ? (
                        <Flex alignItems={"center"} justify={"flex-start"} gap={"10px"}>
                          <span>false</span>
                          <Switch
                            isChecked={rate.to_be_deducted}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleCreditRatesChange(rate.mode, "to_be_deducted", e.target.checked)
                            }
                          />
                          <span>true</span>
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={rate.credit_amount.toString()}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleCreditRatesChange(rate.mode, "credit_amount", e.target.value)
                            }
                          />
                        </Flex>
                      ) : (
                        <Text m={"0px"}>{rate.to_be_deducted.toString()} / {rate.credit_amount} credit(s)</Text>
                      )}
                    </Grid>
                  ))}
                </Stack>
              </Box>
            );
          }

          const visibleKeysForItem = Object.entries(item).filter(([key]) =>
            visibleKeys.includes(key)
          );

          if (visibleKeysForItem.length === 0) {
            // Skip rendering if no keys are visible for this box
            return null;
          }

          return (
            <Box
              key={item.id}
              p={4}
              shadow="md"
              borderWidth="1px"
              mb={4}
              borderRadius={"5px"}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight={700} fontSize="bold" m={"0px"}>
                  {formatConfigName(item.id)}
                </Text>
                {editId === item.id ? (
                  <Flex flexDir={{ base: "column", md: "row" }} gap={{ base: "7px", md: "auto" }}>
                    <Button
                      size={{ base: "sm" }}
                      // leftIcon={<FaSave />}
                      id="btn"
                      variant="solid"
                      onClick={handleSave}
                      isDisabled={!isValid() || isUpdating}
                    >
                      Save
                    </Button>
                    <Button
                      size={{ base: "sm" }}
                      // leftIcon={<FaTimes />}
                      colorScheme="red"
                      variant="outline"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Flex>
                ) : (
                  <IconButton
                    aria-label="Edit"
                    icon={<FaEdit />}
                    onClick={() => handleEditClick(item.id)}
                  />
                )}
              </Flex>
              <Stack spacing={1} mt={3}>
                {Object.entries(item)
                  .filter(([key]) => keysToDisplay.includes(key))
                  .map(([key, value]) => (
                    <Grid
                      gap={{ base: "auto", md: "7px", lg: "auto" }}
                      gridTemplateColumns={{
                        base: "repeat(1, 1fr)",
                        md: "repeat(2, 1fr)",
                      }}
                      key={key}
                    >
                      <Text fontWeight="sm" m={"0px"}>{key.replace(/_/g, " ")}</Text>
                      {editId === item.id ? (
                        key === "Pending_User_License" || key === "Balance_Credit_Pool" ? (
                          <Input value={tempData?.[key] || 0} isDisabled />
                        ) : isBoolean(value) ? (
                          <Flex alignItems={"center"} justify={"flex-start"} gap={"10px"}>
                            {key === 'Intelli_Tag' || key === 'Intelli_Search' || key === 'Domain_by_AI' ? <span>disable</span> : <span>false</span>}
                            <Switch
                              isChecked={tempData?.[key] as boolean}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handleChange(key, e.target.checked, value)
                              }
                            />
                            {key === 'Intelli_Tag' || key === 'Intelli_Search' || key === 'Domain_by_AI' ? <span>enable</span> : <span>true</span>}
                          </Flex>

                        ) : (
                          <Input
                            // value={tempData?.[key] === 0 ? 0 : tempData?.[key]}
                            type="number"
                            step="1"
                            min="0"
                            value={tempData?.[key]?.toString() || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              handleChange(key, e.target.value, value)
                            }
                          />
                        )
                      ) : (
                        <>
                          {(key === 'Intelli_Tag' || key === 'Intelli_Search' || key === 'Domain_by_AI') ? <Text m={"0px"}>{value == true ? "enable" : "disable"}</Text> :
                            <Text m={"0px"}>{value.toString()}</Text>}
                        </>
                      )}

                    </Grid>
                  ))}
                {
                  (editId === item.id && isWarning && warningMsg.length > 0) && <Flex textAlign={"center"} float={"right"} padding={"5px"}>
                    <Text m={"0px"} fontSize={"12px"} fontWeight={"500"} color={"red"}>{warningMsg}</Text>
                  </Flex>
                }
              </Stack>
            </Box>
          );
        })}
      </Grid>
      {role === "superAdmin" && customPromptItem && (
        <Box p={4} shadow="md" borderWidth="1px" mb={4} borderRadius="5px">
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontWeight={700} fontSize="bold" m="0px">
              {formatConfigName(customPromptItem.id)}
            </Text>

            {editId === customPromptItem.id ? (
              <Flex flexDir={{ base: "column", md: "row" }} gap={2}>
                <Button size="sm" id="btn" variant="solid" onClick={handleSave}>
                  Save
                </Button>
                <Button size="sm" colorScheme="red" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </Flex>
            ) : (
              <IconButton aria-label="Edit" icon={<FaEdit />} onClick={() => handleEditClick(customPromptItem.id)} />
            )}
          </Flex>

          {/* Editable company-description pairs */}
          {editId === customPromptItem.id ?
            customPrompts.map((item, index) => (
              <Box key={index} p={2} mt={4} borderWidth="2px" borderRadius="5px">
                <Flex alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center" width="100%">
                    <Text fontWeight="bold" width="12%" mt={1}>Prompt Name</Text>
                    <Input
                      name="company"
                      placeholder="Company name"
                      value={item.Company}
                      width="75%"
                      minWidth="0"
                      onChange={(e) => handleCompanyChange("Company", e.target.value, index)}
                    />
                  </Flex>

                  {/* Delete button on the right side */}
                  {customPrompts.length > 1 && (
                    <IconButton
                      aria-label="Delete"
                      icon={<FaTrash />}
                      colorScheme="red"
                      onClick={() => handleDelete(index)}
                      size="md"
                      variant="ghost"
                    />
                  )}
                </Flex>
                <Box mt={3}>
                  <ReactQuill
                    theme="snow"
                    value={item.Prompt}
                    onChange={(value) => handleCompanyChange("Prompt", value, index)}
                    modules={{ toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }]] }}
                    placeholder="Write something..."
                    style={{ wordBreak: "break-word", width: "96%", maxHeight: "200px", overflowY: "auto" }}
                  />
                </Box>
              </Box>
            )):(
              customPrompts.map((item, index) => (
                <Box key={index} p={2} mt={4} borderWidth="1px" borderRadius="5px">
                  <Flex gap={2}>
                  <Text fontWeight="bold">Prompt Name :</Text>
                  <Text>{item.Company}</Text>
                  </Flex>
                  <Box>
                    <div dangerouslySetInnerHTML={{ __html: item.Prompt }} />
                  </Box>
                </Box>
              ))
            )}

          {editId === customPromptItem.id && (
            <Button onClick={handleAddNew} colorScheme="blue" size="sm" mt={4}>
              Add
            </Button>
          )}
        </Box>
      )}
      {loadingOverlay && <LoadingOverlay message={"Loading"} />}
    </Box>
  );
};

export default Configuration;
